// Skaner kodów QR w przeglądarce, WYŁĄCZNIE z naszego builda.
//
// Port 1:1 logiki z panelu ochrony (innerwebbackend: spa/src/composables/useQrScanner.ts) na
// React — świadomie ta sama strategia, te same kody błędów i ten sam throttle, żeby tablet w sali
// szkoleniowej zachowywał się identycznie jak tablet na bramie.
//
// Strategia detekcji:
// 1. Natywny `BarcodeDetector` (Chrome/Android) — zero dodatkowego kodu, żadnej biblioteki.
// 2. Fallback: `jsqr` (npm, zbundlowane w build) — czysty JS bez WebAssembly, ZERO zależności.
//    Importowane DYNAMICZNIE, więc przeglądarki z natywnym BarcodeDetector nigdy nie ściągają
//    tego kodu w ogóle.
//
// 🔴 Absolutny zakaz CDN — żadnego unpkg/jsdelivr/cdnjs. U klienta on-premise firewall dropuje
// taki ruch PO CICHU: skaner nie zgłosiłby błędu, tylko "wiecznie by się uruchamiał". To dokładnie
// pułapka, w którą wpada domyślna konfiguracja workera pdf.js (patrz scripts/kopiuj-worker-pdf.js
// w tym repo — worker jest kopiowany do public/, nie linkowany z CDN).
import { useCallback, useEffect, useRef, useState } from 'react';

/** Throttle pętli detekcji — Fibonacci, ~11 klatek/s. Wystarczające dla nieruchomego skanu
 * plakietki, oszczędza baterię i CPU tabletu vs. detekcja na KAŻDEJ klatce. */
const SCAN_INTERVAL_MS = 89;

/**
 * DOMException.name → nasze kody błędów. `NotAllowedError`/`PermissionDeniedError`/`SecurityError`
 * = zgoda odmówiona (albo w ogóle nie zapytana, np. polityka firmowa); `NotFoundError`/
 * `DevicesNotFoundError`/`OverconstrainedError` = przeglądarka nie znalazła kamery pasującej do
 * `facingMode`. Wszystko inne (rzadkie: `AbortError`, `NotReadableError` — kamera zajęta przez
 * inną aplikację) ląduje w `unsupported`, żeby użytkownik dostał JAKIŚ komunikat zamiast cichej
 * czarnej ramki.
 */
const classifyGetUserMediaError = (e) => {
  const name = e && e.name;
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'permission_denied';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return 'no_camera';
    default:
      return 'unsupported';
  }
};

const hasNativeBarcodeDetector = () => typeof window !== 'undefined' && 'BarcodeDetector' in window;

export const useQrScanner = () => {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);

  const streamRef = useRef(null);
  const videoRef = useRef(null);
  const intervalRef = useRef(null);
  const detectInFlightRef = useRef(false);
  const detectorRef = useRef(null);
  const jsQrRef = useRef(null);
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  // Pętla detekcji żyje poza cyklem renderowania Reacta (setInterval), więc `scanning` ze stanu
  // byłby w niej zamrożony na wartości z chwili startu. Ref jest jedynym źródłem prawdy dla ticka.
  const activeRef = useRef(false);

  const stop = useCallback(() => {
    activeRef.current = false;
    setScanning(false);
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current = null;
    }
    detectorRef.current = null;
    jsQrRef.current = null;
    canvasRef.current = null;
    ctxRef.current = null;
    detectInFlightRef.current = false;
  }, []);

  const tick = useCallback(async (onDecoded) => {
    // detect() jest asynchroniczne, a setInterval nie czeka na poprzednie wywołanie — bez tej
    // strażniczki wolniejszy tablet nakładałby detekcje na siebie.
    if (detectInFlightRef.current || !activeRef.current) return;
    const video = videoRef.current;
    if (!video || video.readyState < video.HAVE_CURRENT_DATA) return;

    detectInFlightRef.current = true;
    try {
      if (detectorRef.current) {
        const results = await detectorRef.current.detect(video);
        const value = results && results[0] && results[0].rawValue;
        if (value) onDecoded(value);
      } else if (jsQrRef.current && ctxRef.current && canvasRef.current) {
        const width = video.videoWidth;
        const height = video.videoHeight;
        if (width === 0 || height === 0) return;
        if (canvasRef.current.width !== width) canvasRef.current.width = width;
        if (canvasRef.current.height !== height) canvasRef.current.height = height;
        ctxRef.current.drawImage(video, 0, 0, width, height);
        const imageData = ctxRef.current.getImageData(0, 0, width, height);
        const result = jsQrRef.current(imageData.data, imageData.width, imageData.height);
        if (result && result.data) onDecoded(result.data);
      }
    } catch (e) {
      // Detekcja na pojedynczej klatce może się wywalić przejściowo (klatka w trakcie dekodowania
      // wideo) — ignorujemy, kolejny tick spróbuje ponownie.
    } finally {
      detectInFlightRef.current = false;
    }
  }, []);

  const start = useCallback(async (videoElement, onDecoded) => {
    stop();
    setError(null);
    videoRef.current = videoElement;

    // Sprawdzane PRZED getUserMedia — po zwykłym HTTP (typowe dla instalacji on-premise pod
    // adresem IP) getUserMedia w ogóle nie istnieje, a komunikat przeglądarki nie tłumaczy
    // użytkownikowi, że to NIE usterka skanera.
    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      setError('insecure_context');
      return;
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError('unsupported');
      return;
    }

    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
    } catch (e) {
      setError(classifyGetUserMediaError(e));
      return;
    }

    videoElement.srcObject = streamRef.current;
    try {
      await videoElement.play();
    } catch (e) {
      // Autoplay bywa kapryśny mimo `muted` — pętla detekcji i tak czeka na
      // readyState >= HAVE_CURRENT_DATA, więc nie robimy z tego twardego błędu.
    }

    if (hasNativeBarcodeDetector()) {
      detectorRef.current = new window.BarcodeDetector({ formats: ['qr_code'] });
    } else {
      const mod = await import('jsqr');
      jsQrRef.current = mod.default;
      canvasRef.current = document.createElement('canvas');
      ctxRef.current = canvasRef.current.getContext('2d', { willReadFrequently: true });
    }

    activeRef.current = true;
    setScanning(true);
    intervalRef.current = setInterval(() => { tick(onDecoded); }, SCAN_INTERVAL_MS);
  }, [stop, tick]);

  // Odmontowanie MUSI zwolnić kamerę — kamera świecąca się po zamknięciu ekranu jest na tablecie
  // realnym problemem (i na telefonie widoczną kropką "aplikacja Cię nagrywa").
  useEffect(() => stop, [stop]);

  return { scanning, error, start, stop };
};

export default useQrScanner;
